import { ApiError } from "@/shared/api/client"
import type { GiftWithdrawalErrorCode } from "@/shared/api/types"
import type { AppLocale } from "@/shared/i18n/translations"
import { formatAppLocaleDateTimeShort } from "@/shared/lib/formatTournamentStartsAt"
import { useLocaleStore } from "@/shared/store/localeStore"

const KNOWN_WITHDRAWAL_ERROR_CODES = new Set<GiftWithdrawalErrorCode>([
  "gift_transfer_hold",
  "gift_transfer_metadata_missing",
  "gift_not_available",
  "gift_not_found",
  "gift_owner_mismatch",
  "insufficient_gift_withdrawal_fee",
  "relayer_insufficient_stars",
  "insufficient_balance",
])

const INVARIANT_MESSAGE_PREFIX = "Domain invariant violation: "

type TranslateFn = (
  path: string,
  params?: Record<string, string | number>
) => string

export function readGiftWithdrawalErrorCode(
  err: unknown
): GiftWithdrawalErrorCode | null {
  if (!(err instanceof ApiError)) return null
  const raw = (err.details as { code?: unknown } | null | undefined)?.code
  if (
    typeof raw !== "string" ||
    !KNOWN_WITHDRAWAL_ERROR_CODES.has(raw as GiftWithdrawalErrorCode)
  ) {
    return null
  }
  return raw as GiftWithdrawalErrorCode
}

/** Core puts RFC3339 instant after the invariant prefix; format in user's locale/timezone. */
export function readInvariantMessageDetail(message: string): string {
  if (message.startsWith(INVARIANT_MESSAGE_PREFIX)) {
    return message.slice(INVARIANT_MESSAGE_PREFIX.length).trim()
  }
  return message.trim()
}

function formatWithdrawableDateLocal(
  value: Date | number,
  locale: AppLocale
): string {
  const ms = typeof value === "number" ? value : value.getTime()
  return formatAppLocaleDateTimeShort(ms, locale)
}

function parseWithdrawableInstant(detail: string): Date | null {
  const parsed = new Date(detail)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export function formatGiftWithdrawalErrorLabel(
  err: unknown,
  t: TranslateFn,
  gift?: { withdrawable_at?: number | null } | null,
  locale: AppLocale = useLocaleStore.getState().locale
): string {
  const code = readGiftWithdrawalErrorCode(err)

  if (code === "gift_transfer_hold") {
    if (err instanceof ApiError) {
      const instant = parseWithdrawableInstant(
        readInvariantMessageDetail(err.message)
      )
      if (instant) {
        return t("giftDetail.unavailableHold", {
          date: formatWithdrawableDateLocal(instant, locale),
        })
      }
    }
    if (
      gift &&
      typeof gift.withdrawable_at === "number" &&
      gift.withdrawable_at > 0
    ) {
      return t("giftDetail.unavailableHold", {
        date: formatWithdrawableDateLocal(gift.withdrawable_at, locale),
      })
    }
    return t("giftDetail.unavailableHoldNoDate")
  }

  switch (code) {
    case "insufficient_gift_withdrawal_fee":
    case "insufficient_balance":
      return t("giftDetail.insufficientFunds")
    case "gift_not_available":
      return t("giftDetail.unavailableInRound")
    case "gift_transfer_metadata_missing":
      return t("giftDetail.unavailableMetadata")
    case "relayer_insufficient_stars":
      return t("giftDetail.relayerInsufficientStars")
    default:
      return t("giftDetail.withdrawalFailed")
  }
}
